import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiPlus, FiTrash2, FiEdit2, FiList, FiUserPlus, FiX, FiUpload } from 'react-icons/fi';
import { useConfirm } from '../../ui/ConfirmDialog';

const Lists = () => {
  const confirm = useConfirm();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState(null);
  const [listMembers, setListMembers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '' });
  const [addEmailsRaw, setAddEmailsRaw] = useState('');

  const loadLists = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/emails/lists');
      setLists(r.data.lists);
    } catch (error) {
      toast.error('Failed to load lists');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async (listId) => {
    try {
      const r = await axios.get(`/api/admin/emails/lists/${listId}/members`);
      setListMembers(r.data.contacts || []);
    } catch (error) {
      toast.error('Failed to load members');
    }
  };

  useEffect(() => { loadLists(); }, []);
  useEffect(() => {
    if (selectedList) loadMembers(selectedList.id);
    else setListMembers([]);
  }, [selectedList]);

  const createList = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) return;
    try {
      const r = await axios.post('/api/admin/emails/lists', createForm);
      toast.success('List created');
      setShowCreate(false);
      setCreateForm({ name: '', description: '' });
      setLists([{ ...r.data.list, memberCount: 0 }, ...lists]);
      setSelectedList(r.data.list);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create list');
    }
  };

  const deleteList = async (id) => {
    const ok = await confirm({
      title: 'Delete this list?',
      description: 'Members are removed from the list but the contacts themselves are kept.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/admin/emails/lists/${id}`);
      setLists(lists.filter(l => l.id !== id));
      if (selectedList?.id === id) setSelectedList(null);
      toast.success('List deleted');
    } catch (error) {
      toast.error('Failed to delete list');
    }
  };

  const addMembers = async (e) => {
    e.preventDefault();
    const emails = addEmailsRaw
      .split(/[\s,;\n]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
    if (emails.length === 0) return;
    try {
      const r = await axios.post(`/api/admin/emails/lists/${selectedList.id}/members`, { emails });
      toast.success(`Added ${r.data.added} new member${r.data.added === 1 ? '' : 's'}`);
      setAddEmailsRaw('');
      loadMembers(selectedList.id);
      loadLists();
    } catch (error) {
      toast.error('Failed to add members');
    }
  };

  const removeMember = async (contactId) => {
    try {
      await axios.delete(`/api/admin/emails/lists/${selectedList.id}/members/${contactId}`);
      setListMembers(listMembers.filter(m => m.id !== contactId));
      loadLists();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const importCsvToList = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedList) return;
    try {
      const text = await file.text();
      // Simple: split lines, first column = email
      const emails = text.split(/\r?\n/)
        .slice(1) // skip header
        .map(l => l.split(',')[0].replace(/"/g, '').trim())
        .filter(e => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
      if (emails.length === 0) {
        toast.error('No valid email addresses found in file');
        return;
      }
      // Import them globally first, then add to list
      await axios.post('/api/admin/emails/contacts/import', { rows: emails.map(email => ({ email })) });
      const r = await axios.post(`/api/admin/emails/lists/${selectedList.id}/members`, { emails });
      toast.success(`Added ${r.data.added} new members to list`);
      loadMembers(selectedList.id);
      loadLists();
    } catch (error) {
      toast.error('CSV import failed');
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      {/* Lists column */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Email Lists</h3>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded text-xs font-medium transition-colors"
          >
            <FiPlus className="w-3 h-3" />
            New
          </button>
        </div>

        {showCreate && (
          <form onSubmit={createList} className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 space-y-2">
            <input
              type="text"
              required
              placeholder="List name"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              className="w-full px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
              autoFocus
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              className="w-full px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setShowCreate(false); setCreateForm({ name: '', description: '' }); }}
                className="flex-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-2.5 py-1.5 text-xs font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
              >
                Create
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="p-6 text-center text-xs text-gray-500 dark:text-gray-400">Loading…</div>
        ) : lists.length === 0 ? (
          <div className="p-8 text-center">
            <FiList className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No lists yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {lists.map(list => (
              <button
                key={list.id}
                onClick={() => setSelectedList(list)}
                className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                  selectedList?.id === list.id ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-primary-600' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{list.name}</div>
                    {list.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{list.description}</div>
                    )}
                  </div>
                  <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">
                    {list.memberCount ?? 0}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Members column */}
      <div className="lg:col-span-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
        {selectedList ? (
          <>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{selectedList.name}</h3>
                {selectedList.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{selectedList.description}</p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                  {listMembers.length} member{listMembers.length === 1 ? '' : 's'}
                </span>
                <label className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  <FiUpload className="w-3 h-3" />
                  CSV
                  <input type="file" accept=".csv,text/csv" onChange={importCsvToList} className="hidden" />
                </label>
                <button
                  onClick={() => deleteList(selectedList.id)}
                  className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                  title="Delete list"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <form onSubmit={addMembers} className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Add emails (space, comma or newline separated)
              </label>
              <textarea
                value={addEmailsRaw}
                onChange={(e) => setAddEmailsRaw(e.target.value)}
                placeholder="alice@example.com, bob@example.com"
                rows={2}
                className="w-full px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white resize-none"
              />
              <div className="flex justify-end mt-1.5">
                <button
                  type="submit"
                  disabled={!addEmailsRaw.trim()}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-md text-xs font-medium transition-colors"
                >
                  <FiUserPlus className="w-3 h-3" />
                  Add to list
                </button>
              </div>
            </form>

            {listMembers.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">No members yet. Add some above.</p>
              </div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                    {listMembers.map(m => (
                      <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-2 text-xs text-gray-900 dark:text-white">{m.email}</td>
                        <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                          {[m.firstName || m.first_name, m.lastName || m.last_name].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td className="px-4 py-2">
                          {(m.unsubscribedAt || m.unsubscribed_at) ? (
                            <span className="text-xs text-red-600 dark:text-red-400">Unsubscribed</span>
                          ) : (
                            <span className="text-xs text-emerald-600 dark:text-emerald-400">Active</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => removeMember(m.id)}
                            className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                            title="Remove from list"
                          >
                            <FiX className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div className="p-10 text-center">
            <FiList className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Select a list to see and manage its members</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Lists;
